import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { Trash2, Plus, Pencil, Search, Loader2 } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';
import { InlineConfirmation } from '../ui/InlineConfirmation';

interface VaultSecretDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface VaultSecret {
  key: string;
  // Value is never shown for security
}

export function VaultSecretDialog({ open, onClose, onSuccess }: VaultSecretDialogProps) {
  const [secrets, setSecrets] = useState<VaultSecret[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'list' | 'create' | 'update'>('list');
  const [selectedKey, setSelectedKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmingDeleteKey, setConfirmingDeleteKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && mode === 'list') {
      loadSecrets();
    }
  }, [open, mode]);

  const loadSecrets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.listVaultSecrets();
      setSecrets(data.secrets || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load vault secrets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setMode('create');
    setSecretKey('');
    setSecretValue('');
    setError(null);
  };

  const handleUpdate = (key: string) => {
    setMode('update');
    setSelectedKey(key);
    setSecretKey(key);
    setSecretValue('');
    setError(null);
  };

  const handleSave = async () => {
    if (!secretKey.trim() || !secretValue.trim()) {
      setError('Both key and value are required');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      await apiClient.saveVaultSecret(secretKey, secretValue);
      // Reset form and go back to list mode
      setSecretKey('');
      setSecretValue('');
      setSelectedKey('');
      setMode('list');
      // Explicitly reload secrets to ensure the list is updated
      await loadSecrets();
    } catch (err: any) {
      setError(err.message || 'Failed to save vault secret');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    setDeleting(key);
    setError(null);
    
    try {
      await apiClient.deleteVaultSecret(key);
      await loadSecrets();
    } catch (err: any) {
      setError(err.message || 'Failed to delete vault secret');
    } finally {
      setDeleting(null);
      setConfirmingDeleteKey(null);
    }
  };

  const handleClose = () => {
    if (!saving && !deleting) {
      setMode('list');
      setSecretKey('');
      setSecretValue('');
      setSelectedKey('');
      setError(null);
      setSearchQuery('');
      onClose();
    }
  };

  const handleCancel = () => {
    setMode('list');
    setSecretKey('');
    setSecretValue('');
    setSelectedKey('');
    setError(null);
  };

  // Filter secrets based on search query
  const filteredSecrets = secrets.filter(secret =>
    secret.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'list' && 'Manage Vault Secrets'}
            {mode === 'create' && 'Create New Vault Secret'}
            {mode === 'update' && 'Update Vault Secret'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'list' && 'Manage secrets stored in OCI Vault'}
            {mode === 'create' && 'Create a new secret in OCI Vault'}
            {mode === 'update' && 'Update an existing secret in OCI Vault'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {mode === 'list' && (
            <>
            <div className="mb-4 flex items-center gap-3">
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create New Secret
              </button>

              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search secrets..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            {confirmingDeleteKey && (
              <InlineConfirmation
                message={`Delete secret "${confirmingDeleteKey}"? This cannot be undone.`}
                confirmText="Delete"
                onConfirm={() => handleDelete(confirmingDeleteKey)}
                onCancel={() => setConfirmingDeleteKey(null)}
              />
            )}

            {/* Scrollable area for secrets list */}
            <div className="overflow-y-auto max-h-[500px]">
              {loading ? (
                <div className="space-y-2 py-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredSecrets.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {searchQuery ? `No secrets matching "${searchQuery}"` : 'No vault secrets found'}
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Secret Key
                          {searchQuery && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              ({filteredSecrets.length} of {secrets.length})
                            </span>
                          )}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredSecrets.map((secret) => (
                        <tr key={secret.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">
                            {secret.key}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleUpdate(secret.key)}
                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Update secret value"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setConfirmingDeleteKey(secret.key)}
                                disabled={deleting === secret.key}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Delete secret"
                              >
                                {deleting === secret.key ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
            </>
          )}

          {(mode === 'create' || mode === 'update') && (
            <div className="space-y-4">
            <div>
              <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Secret Key
              </label>
              <input
                id="secretKey"
                type="text"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="e.g., tenant-aws-access-key-id"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                disabled={saving || mode === 'update'}
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Use hyphens for multi-word keys (e.g., master-oci-user-id, tenant-aws-secret-key)
              </p>
            </div>

            <div>
              <label htmlFor="secretValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Secret Value
              </label>
              <textarea
                id="secretValue"
                value={secretValue}
                onChange={(e) => setSecretValue(e.target.value)}
                placeholder="Enter secret value"
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none resize-y font-mono"
                disabled={saving}
              />
              {mode === 'update' && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Enter a new value to update this secret
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
          </div>
          )}
        </DialogBody>

        <DialogFooter>
          {mode === 'list' ? (
            <Button variant="secondary" onClick={handleClose} disabled={saving || !!deleting}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={saving || !secretKey.trim() || !secretValue.trim()}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  mode === 'create' ? 'Create Secret' : 'Update Secret'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

